/**
 * AssessmentGenerator Component
 * ORCA360 - AI Assessment Generation UI
 * 
 * UI component for triggering and monitoring AI-powered assessment generation
 * Displays progress, validation statistics, and handles success/error states
 * 
 * Sprint 3, Step 2, Phase 3
 * Updated: Sprint 4, Week 1 - Multi-transcript preview (MAD-16)
 */

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import {
  useAssessmentGeneration,
  getStageLabel,
  getValidationIcon,
  GenerationStage,
  type AssessmentGenerationResult,
  type AssessmentType,
} from '../hooks/useAssessmentGeneration';
import { TranscriptPreview, type Transcript } from './TranscriptPreview';
import { BackgroundJobBadges } from './BackgroundJobTimeline';

/**
 * Component props
 */
export interface AssessmentGeneratorProps {
  sessionId: string;
  onComplete?: (result: AssessmentGenerationResult) => void;
  onError?: (error: Error) => void;
  className?: string;
  refreshKey?: number; // Optional: increment to force transcript refresh
  onRunEventDetection?: () => void; // Callback to trigger event detection from parent
  hideHeader?: boolean; // Hide the component header when used inside a parent card with its own title
  transcriptVariant?: 'default' | 'white'; // Style variant for transcript preview
}

/**
 * AssessmentGenerator Component
 */
export function AssessmentGenerator({
  sessionId,
  onComplete,
  onError,
  className = '',
  refreshKey = 0,
  onRunEventDetection,
  hideHeader = false,
  transcriptVariant = 'default',
}: AssessmentGeneratorProps) {
  const { generateAssessments, isGenerating, progress, stage, error, result } =
    useAssessmentGeneration();

  // Track which assessment type is currently being generated
  const [currentAssessmentType, setCurrentAssessmentType] = useState<AssessmentType | null>(null);
  
  // Track if we're generating both assessments (pilot then instructor)
  const [isGeneratingBoth, setIsGeneratingBoth] = useState(false);
  const [bothPhase, setBothPhase] = useState<1 | 2>(1); // 1 = pilot, 2 = instructor

  // Track which assessment types already exist for this session
  const [existingAssessments, setExistingAssessments] = useState<{
    pilot: boolean;
    instructor: boolean;
  }>({ pilot: false, instructor: false });

  // MAD-16: Fetch transcripts for preview
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [loadingTranscripts, setLoadingTranscripts] = useState(true);

  // Event detection warning state
  const [existingEventsCount, setExistingEventsCount] = useState<number | null>(null);
  const [showEventWarning, setShowEventWarning] = useState(false);
  const [pendingAssessmentType, setPendingAssessmentType] = useState<AssessmentType | null>(null);
  const [pendingBothMode, setPendingBothMode] = useState(false); // Track if warning is for "both" mode

  // Fetch existing events count on mount
  useEffect(() => {
    const fetchEventsCount = async () => {
      try {
        const { count, error: fetchError } = await supabase
          .from('events')
          .select('id', { count: 'exact', head: true })
          .eq('session_id', sessionId)
          .eq('dismissed', false);

        if (fetchError) {
          console.error('[AssessmentGenerator] Error fetching events count:', fetchError);
          setExistingEventsCount(null);
        } else {
          setExistingEventsCount(count ?? 0);
        }
      } catch (err) {
        console.error('[AssessmentGenerator] Exception fetching events count:', err);
        setExistingEventsCount(null);
      }
    };

    fetchEventsCount();
  }, [sessionId, refreshKey]);

  // Check which assessment types already exist for this session
  useEffect(() => {
    const checkExisting = async () => {
      try {
        const [pilotResult, instructorResult] = await Promise.all([
          supabase
            .from('assessments_pilot')
            .select('id', { count: 'exact', head: true })
            .eq('session_id', sessionId),
          supabase
            .from('assessments_instructor')
            .select('id', { count: 'exact', head: true })
            .eq('session_id', sessionId),
        ]);

        setExistingAssessments({
          pilot: (pilotResult.count ?? 0) > 0,
          instructor: (instructorResult.count ?? 0) > 0,
        });
      } catch (err) {
        console.error('[AssessmentGenerator] Error checking existing assessments:', err);
      }
    };

    checkExisting();
  }, [sessionId, refreshKey]);

  /**
   * Fetch transcripts for preview
   * NOTE: Always fetch ALL transcripts for the session, regardless of transcriptId
   * The transcriptId is only used for UI selection/highlighting, not filtering
   * All transcripts will be used for assessment generation
   */
  useEffect(() => {
    const fetchTranscripts = async () => {
      setLoadingTranscripts(true);
      try {
        // Always fetch ALL transcripts for this session
        // Assessment generation uses all transcripts, not just the selected one
        // Filter: Only show COMPLETED and NON-ARCHIVED transcripts
        const { data, error } = await supabase
          .from('transcripts')
          .select(`
            id,
            anonymized_text,
            duration_seconds,
            file_id,
            created_at,
            processing_status,
            is_archived,
            files:file_id (
              file_name,
              file_type
            )
          `)
          .eq('session_id', sessionId)
          .eq('processing_status', 'completed')
          .or('is_archived.is.null,is_archived.eq.false')
          .order('created_at', { ascending: true });

        if (error) {
          console.error('[AssessmentGenerator] Error fetching transcripts:', error);
        } else {
          // Transform data to flatten file information
          const transformedTranscripts = (data || []).map((transcript) => {
            // Handle files as either object or array (Supabase can return either)
            const fileData = Array.isArray(transcript.files) ? transcript.files[0] : transcript.files;

            return {
              ...transcript,
              file_name: fileData?.file_name || null,
              file_type: fileData?.file_type || null,
              files: undefined, // Remove nested object
            };
          });

          setTranscripts(transformedTranscripts);
        }
      } catch (err) {
        console.error('[AssessmentGenerator] Exception fetching transcripts:', err);
      } finally {
        setLoadingTranscripts(false);
      }
    };

    fetchTranscripts();
  }, [sessionId, refreshKey]); // Re-fetch when sessionId or refreshKey changes

  /**
   * Handle generate button click
   * @param assessmentType - 'pilot' or 'instructor'
   */
  const handleGenerate = async (assessmentType: AssessmentType) => {
    // Check for existing events before pilot assessment generation
    // Events provide context for RCA and SMART Goals
    if (assessmentType === 'pilot' && existingEventsCount === 0) {
      setPendingAssessmentType(assessmentType);
      setPendingBothMode(false); // Single assessment, not "both" mode
      setShowEventWarning(true);
      return;
    }

    await executeGeneration(assessmentType);
  };

  /**
   * Execute the actual assessment generation
   * Called directly or after user confirms to skip event detection
   */
  const executeGeneration = async (assessmentType: AssessmentType) => {
    try {
      setCurrentAssessmentType(assessmentType);
      const generationResult = await generateAssessments(sessionId, assessmentType);
      if (onComplete) {
        onComplete(generationResult);
      }
    } catch (err) {
      if (onError && err instanceof Error) {
        onError(err);
      }
    } finally {
      setCurrentAssessmentType(null);
    }
  };

  /**
   * Handle user choosing to run event detection first
   */
  const handleRunEventDetection = () => {
    setShowEventWarning(false);
    setPendingAssessmentType(null);
    if (onRunEventDetection) {
      onRunEventDetection();
    }
  };

  /**
   * Handle user choosing to skip event detection
   */
  const handleSkipEventDetection = () => {
    setShowEventWarning(false);
    
    if (pendingBothMode) {
      // User was trying to generate both, continue with both
      setPendingBothMode(false);
      setPendingAssessmentType(null);
      executeGenerateBoth();
    } else if (pendingAssessmentType) {
      // Single assessment mode
      executeGeneration(pendingAssessmentType);
      setPendingAssessmentType(null);
    }
  };

  /**
   * Handle generating both pilot and instructor assessments sequentially
   */
  const handleGenerateBoth = async () => {
    // Check for existing events before pilot assessment generation
    if (existingEventsCount === 0) {
      setPendingAssessmentType('pilot');
      setPendingBothMode(true); // Mark that we're in "both" mode
      setShowEventWarning(true);
      return;
    }

    await executeGenerateBoth();
  };

  /**
   * Execute generation of both assessments (pilot first, then instructor)
   */
  const executeGenerateBoth = async () => {
    setIsGeneratingBoth(true);
    setBothPhase(1);
    
    try {
      // Phase 1: Generate pilot assessment
      setCurrentAssessmentType('pilot');
      await generateAssessments(sessionId, 'pilot');
      
      // Phase 2: Generate instructor assessment
      setBothPhase(2);
      setCurrentAssessmentType('instructor');
      const instructorResult = await generateAssessments(sessionId, 'instructor');
      
      // Call onComplete with the final result
      if (onComplete) {
        onComplete(instructorResult);
      }
    } catch (err) {
      if (onError && err instanceof Error) {
        onError(err);
      }
    } finally {
      setIsGeneratingBoth(false);
      setCurrentAssessmentType(null);
      setBothPhase(1);
    }
  };


  /**
   * Render validation statistics
   */
  const renderValidationStats = () => {
    if (!result || !result.metadata.validationStats) {
      return null;
    }

    const rawStats = result.metadata.validationStats;

    // Handle both array and object formats, provide safe defaults
    // Defensively check for null/undefined before accessing array index
    const statsData =
      Array.isArray(rawStats) && rawStats.length > 0 && rawStats[0]
        ? rawStats[0]
        : (rawStats && !Array.isArray(rawStats) ? rawStats : null);

    // Ensure all required fields exist with safe defaults
    const stats = {
      verified: statsData?.verified ?? 0,
      paraphrased: statsData?.paraphrased ?? 0,
      similar: statsData?.similar ?? 0,
      unverified: statsData?.unverified ?? 0,
      verificationRate: statsData?.verificationRate ?? 0,
      avgConfidence: statsData?.avgConfidence ?? 0,
      total: statsData?.total ?? 0,
    };

    return (
      <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">
          Evidence Validation Results
        </h4>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{stats.verified}</div>
            <div className="text-xs text-slate-600 dark:text-slate-400">
              {getValidationIcon('VERIFIED')} Verified
            </div>
          </div>

          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">{stats.paraphrased}</div>
            <div className="text-xs text-slate-600 dark:text-slate-400">
              {getValidationIcon('PARAPHRASED')} Paraphrased
            </div>
          </div>

          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">{stats.similar}</div>
            <div className="text-xs text-slate-600 dark:text-slate-400">
              {getValidationIcon('SIMILAR')} Similar
            </div>
          </div>

          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{stats.unverified}</div>
            <div className="text-xs text-slate-600 dark:text-slate-400">
              {getValidationIcon('UNVERIFIED')} Unverified
            </div>
          </div>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-600 dark:text-slate-400">Verification Rate:</span>
            <span
              className={`font-semibold ${
                stats.verificationRate >= 90
                  ? 'text-green-600'
                  : stats.verificationRate >= 75
                  ? 'text-yellow-600'
                  : 'text-red-600'
              }`}
            >
              {stats.verificationRate.toFixed(1)}%
            </span>
          </div>

          <div className="flex justify-between">
            <span className="text-slate-600 dark:text-slate-400">Avg Confidence:</span>
            <span className="font-semibold text-slate-900 dark:text-slate-100">
              {(stats.avgConfidence * 100).toFixed(1)}%
            </span>
          </div>

          <div className="flex justify-between">
            <span className="text-slate-600 dark:text-slate-400">Total Evidence:</span>
            <span className="font-semibold text-slate-900 dark:text-slate-100">{stats.total}</span>
          </div>
        </div>

        {stats.verificationRate < 90 && (
          <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-xs text-yellow-800 dark:text-yellow-200">
            ⚠️ Verification rate is below 90%. Instructor should review unverified evidence.
          </div>
        )}
      </div>
    );
  };

  /**
   * Render processing time
   */
  const renderProcessingTime = () => {
    if (!result) {
      return null;
    }

    const time = result.metadata.processingTime;

    return (
      <div className="mt-4 text-xs text-slate-600 dark:text-slate-400 space-y-1">
        <div>Evidence Extraction: {(time.evidenceExtraction / 1000).toFixed(1)}s</div>
        <div>Assessment Generation: {(time.assessmentGeneration / 1000).toFixed(1)}s</div>
        <div>Database Storage: {(time.databaseStorage / 1000).toFixed(1)}s</div>
        <div>Evidence Validation: {(time.evidenceValidation / 1000).toFixed(1)}s</div>
        <div className="font-semibold text-slate-900 dark:text-slate-100">
          Total: {(time.total / 1000).toFixed(1)}s
        </div>
      </div>
    );
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Header */}
      {!hideHeader && (
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              AI Assessment Generation
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Generate competency-based assessments using AI
            </p>
          </div>
        </div>
      )}

      {/* MAD-16: Transcript Preview - only show when not generating and no result */}
      {!result && !isGenerating && (
        <>
          {loadingTranscripts ? (
            <div className="text-sm text-slate-500 dark:text-slate-400">Loading transcripts...</div>
          ) : (
            <TranscriptPreview transcripts={transcripts} variant={transcriptVariant} />
          )}
        </>
      )}

      {/* Assessment Generation Buttons - shown when transcripts exist and assessments not yet generated */}
      {!isGenerating && transcripts.length > 0 && !existingAssessments.pilot && !existingAssessments.instructor && (
        <div className="space-y-2 ml-10">
          {/* Primary: Generate Both Button - full width to match TranscriptPreview */}
          <button
            onClick={handleGenerateBoth}
            className="w-full px-4 py-2 bg-orca-elevate text-white text-sm rounded-md hover:bg-orca-elevate/90 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            disabled={isGenerating}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Generate Pilot & Instructor Assessments
          </button>

          {/* Secondary: Individual buttons */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 dark:text-slate-400">Or generate individually:</span>
            <button
              onClick={() => handleGenerate('pilot')}
              className="px-3 py-1 bg-navy text-white text-xs rounded-md hover:bg-navy/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              disabled={isGenerating}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Pilot Only
            </button>
            <button
              onClick={() => handleGenerate('instructor')}
              className="px-3 py-1 bg-navy text-white text-xs rounded-md hover:bg-navy/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              disabled={isGenerating}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              Instructor Only
            </button>
          </div>
        </div>
      )}

      {/* Show only the remaining assessment type button when one already exists */}
      {!isGenerating && transcripts.length > 0 && existingAssessments.pilot && !existingAssessments.instructor && (
        <div className="space-y-2 ml-10">
          <button
            onClick={() => handleGenerate('instructor')}
            className="w-full px-4 py-2 bg-navy text-white text-sm rounded-md hover:bg-navy/90 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            disabled={isGenerating}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            Generate Instructor Assessment
          </button>
        </div>
      )}

      {!isGenerating && transcripts.length > 0 && !existingAssessments.pilot && existingAssessments.instructor && (
        <div className="space-y-2 ml-10">
          <button
            onClick={() => handleGenerate('pilot')}
            className="w-full px-4 py-2 bg-navy text-white text-sm rounded-md hover:bg-navy/90 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            disabled={isGenerating}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Generate Pilot Assessment
          </button>
        </div>
      )}

      {/* Progress bar */}
      {isGenerating && (
        <div className="space-y-2">
          {/* Phase indicator for "both" mode */}
          {isGeneratingBoth && (
            <div className="flex items-center gap-2 text-sm font-medium text-orca-elevate mb-1">
              <span className="px-2 py-0.5 bg-orca-elevate/10 rounded-full">
                Step {bothPhase} of 2
              </span>
              <span className="text-slate-600 dark:text-slate-400">
                {bothPhase === 1 ? 'Generating Pilot Assessment...' : 'Generating Instructor Assessment...'}
              </span>
            </div>
          )}
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-700 dark:text-slate-300">
              {currentAssessmentType === 'instructor' ? '👨‍🏫 Instructor: ' : '👨‍✈️ Pilot: '}
              {getStageLabel(stage)}
            </span>
            <span className="text-slate-600 dark:text-slate-400 font-mono">{progress}%</span>
          </div>

          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3 overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ease-out ${
                currentAssessmentType === 'instructor'
                  ? 'bg-gradient-to-r from-orca-elevate to-orca-elevate/70'
                  : 'bg-gradient-to-r from-navy to-navy/70'
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="text-xs text-slate-500 dark:text-slate-400">
            {isGeneratingBoth 
              ? `Generating ${currentAssessmentType === 'instructor' ? 'instructor' : 'pilot'} assessment (${bothPhase}/2)... This may take 2-4 minutes total.`
              : `Generating ${currentAssessmentType === 'instructor' ? 'instructor' : 'pilot'} assessment... This may take 1-2 minutes.`
            }
          </div>
        </div>
      )}

      {/* Error state */}
      {error && stage === GenerationStage.ERROR && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-start space-x-3">
            <span className="text-red-500 text-xl">❌</span>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-900 dark:text-red-100">
                Assessment Generation Failed
              </h4>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
              <div className="mt-3 flex gap-3">
                <button
                  onClick={() => handleGenerate('pilot')}
                  className="text-sm text-red-600 dark:text-red-400 underline hover:no-underline"
                >
                  Retry Pilot Assessment
                </button>
                <button
                  onClick={() => handleGenerate('instructor')}
                  className="text-sm text-red-600 dark:text-red-400 underline hover:no-underline"
                >
                  Retry Instructor Assessment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success state */}
      {result && stage === GenerationStage.COMPLETE && (
        <div className="space-y-4">
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-start space-x-3">
              <span className="text-green-500 text-xl">✅</span>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-green-900 dark:text-green-100">
                  Assessments Generated Successfully!
                </h4>
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  Generated {result.metadata.storedCount} competency assessments with{' '}
                  {result.metadata.evidenceCount} pieces of evidence.
                </p>
              </div>
            </div>
          </div>

          {/* Template source warnings */}
          {result.templateWarnings && result.templateWarnings.length > 0 && (
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <div className="flex items-start space-x-3">
                <span className="text-yellow-500 text-xl">⚠️</span>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-yellow-900 dark:text-yellow-100">
                    Template Warning
                  </h4>
                  <div className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                    {result.templateWarnings.map((warning, index) => (
                      <p key={index}>{warning}</p>
                    ))}
                  </div>
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
                    Using hardcoded fallback templates may result in less accurate grades.
                    Contact your administrator to configure proper assessment templates.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Template source indicators */}
          {result.templateSources && (
            <div className="flex flex-wrap gap-2 text-xs">
              {Object.entries(result.templateSources).map(([useCase, source]) => (
                <div
                  key={useCase}
                  className={`px-2 py-1 rounded ${
                    source === 'fallback'
                      ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                      : source === 'global'
                      ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                  }`}
                >
                  {useCase.replace(/_/g, ' ')}: {source}
                </div>
              ))}
            </div>
          )}

          {/* Assessment summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
              <div className="text-xs text-slate-600 dark:text-slate-400 mb-1">Competencies</div>
              <div className="text-xl font-bold text-slate-900 dark:text-slate-100">
                {result.metadata.competenciesAssessed}
              </div>
            </div>

            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
              <div className="text-xs text-slate-600 dark:text-slate-400 mb-1">Avg Grade</div>
              <div className="text-xl font-bold text-slate-900 dark:text-slate-100">
                {result.metadata.avgScore.toFixed(1)} / 5
              </div>
            </div>

            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
              <div className="text-xs text-slate-600 dark:text-slate-400 mb-1">Evidence Items</div>
              <div className="text-xl font-bold text-slate-900 dark:text-slate-100">
                {result.metadata.evidenceCount}
              </div>
            </div>

            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
              <div className="text-xs text-slate-600 dark:text-slate-400 mb-1">Processing Time</div>
              <div className="text-xl font-bold text-slate-900 dark:text-slate-100">
                {(result.metadata.processingTime.total / 1000).toFixed(1)}s
              </div>
            </div>
          </div>

          {/* Validation statistics */}
          {renderValidationStats()}

          {/* Background Job Status */}
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-start gap-3 mb-3">
              <span className="text-blue-500 text-xl">🔄</span>
              <div>
                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                  Background Jobs Started
                </h4>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                  Evidence validation, RCA, and SMART goals are processing on the server.
                  You can safely navigate away or close this page.
                </p>
              </div>
            </div>
            <div className="mt-3">
              <BackgroundJobBadges sessionId={sessionId} assessmentType="pilot" />
            </div>
          </div>

          {/* Processing time breakdown */}
          {renderProcessingTime()}
        </div>
      )}

      {/* Event Detection Warning Dialog */}
      {showEventWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="relative bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-md mx-4">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  No Events Detected
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  Event detection hasn't been run for this session.
                </p>
              </div>
            </div>
            
            <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3 mb-4">
              <p className="text-sm text-slate-700 dark:text-slate-300">
                <strong>Why does this matter?</strong>
              </p>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                Detected events provide context for <strong>Root Cause Analysis (RCA)</strong> and <strong>SMART Goals</strong> in pilot assessments. Running event detection first can improve the quality of these sections.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleRunEventDetection}
                className="flex-1 px-4 py-2.5 bg-sky-600 hover:bg-sky-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Run Event Detection First
              </button>
              <button
                onClick={handleSkipEventDetection}
                className="flex-1 px-4 py-2.5 bg-slate-200 hover:bg-slate-300 dark:bg-slate-600 dark:hover:bg-slate-500 text-slate-700 dark:text-slate-200 font-medium rounded-lg transition-colors"
              >
                Skip and Continue
              </button>
            </div>

            <button
              onClick={() => { setShowEventWarning(false); setPendingAssessmentType(null); setPendingBothMode(false); }}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


